import React, { useEffect, useRef, useState } from 'react'

// Single-series column chart (inline SVG, no chart library).
// data: [{ key, label (x-axis), title (tooltip heading), value, detail (optional tooltip line) }]
// Marks follow the dataviz rules: one brand hue, bars <= 24px with a 4px rounded top, hairline grid,
// round y ticks, a per-bar tooltip (hover, tap or arrow keys) and a hidden table for screen readers.

const MAX_BAR = 24
const AXIS_W = 30
const AXIS_H = 22
const TOP_PAD = 18

// 0..max split into ~4 round steps (1, 2, 5 × 10^n)
const niceTicks = (max) => {
    if (max <= 0) return [0, 1]
    const raw = max / 4
    const pow = Math.pow(10, Math.floor(Math.log10(raw)))
    const step = [1, 2, 5, 10].map(m => m * pow).find(s => s >= raw) || 10 * pow
    const ticks = []
    for (let v = 0; v <= max + step * 0.001; v += step) ticks.push(Math.round(v))
    if (ticks[ticks.length - 1] < max) ticks.push(ticks[ticks.length - 1] + step)
    return ticks
}

// column with a 4px rounded top and a square base
const barPath = (x, y, w, h) => {
    const r = Math.min(4, w / 2, h)
    return `M${x},${y + h} V${y + r} Q${x},${y} ${x + r},${y} H${x + w - r} Q${x + w},${y} ${x + w},${y + r} V${y + h} Z`
}

const BarChart = ({ data, height = 220, ariaLabel, unit = '', compact = false, labelKeys = [] }) => {
    const wrapRef = useRef(null)
    const [width, setWidth] = useState(0)
    const [active, setActive] = useState(-1)

    useEffect(() => {
        const el = wrapRef.current
        if (!el) return undefined
        const measure = () => setWidth(el.clientWidth)
        measure()
        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', measure)
            return () => window.removeEventListener('resize', measure)
        }
        const ro = new ResizeObserver(measure)
        ro.observe(el)
        return () => ro.disconnect()
    }, [])

    const axisW = compact ? 0 : AXIS_W
    const axisH = compact ? 0 : AXIS_H
    const top = compact ? 2 : TOP_PAD
    const plotW = Math.max(0, width - axisW)
    const plotH = height - axisH - top
    const max = Math.max(0, ...data.map(d => d.value || 0))
    const ticks = compact ? [0, Math.max(1, max)] : niceTicks(max)
    const yMax = ticks[ticks.length - 1] || 1
    const band = data.length ? plotW / data.length : 0
    const barW = Math.max(2, Math.min(MAX_BAR, band - 2))
    const y = (v) => top + plotH - (v / yMax) * plotH
    // show every nth x label so labels never collide (~38px each)
    const every = Math.max(1, Math.ceil(data.length * 38 / Math.max(plotW, 1)))

    const onKey = (e) => {
        if (!data.length) return
        if (e.key === 'ArrowRight') { setActive(i => Math.min(data.length - 1, i < 0 ? data.length - 1 : i + 1)); e.preventDefault() }
        if (e.key === 'ArrowLeft') { setActive(i => Math.max(0, i < 0 ? data.length - 1 : i - 1)); e.preventDefault() }
        if (e.key === 'Escape') setActive(-1)
    }

    const tip = active >= 0 && data[active]
    const tipX = tip ? axisW + band * active + band / 2 : 0

    return (
        <div className={'mv-chart' + (compact ? ' is-compact' : '')} ref={wrapRef}
            tabIndex={compact ? -1 : 0} onKeyDown={onKey} onMouseLeave={() => setActive(-1)} onBlur={() => setActive(-1)}>
            {width > 0 &&
                <svg width={width} height={height} role='img' aria-label={ariaLabel} focusable='false'>
                    {!compact && ticks.map(t =>
                        <g key={t}>
                            <line x1={axisW} x2={width} y1={y(t)} y2={y(t)} className='mv-chart-grid' />
                            <text x={axisW - 6} y={y(t)} className='mv-chart-tick' textAnchor='end' dominantBaseline='middle'>
                                {t.toLocaleString('en-IN')}
                            </text>
                        </g>
                    )}
                    {data.map((d, i) => {
                        const v = d.value || 0
                        const h = (v / yMax) * plotH
                        const x = axisW + band * i + (band - barW) / 2
                        const dim = active >= 0 && active !== i
                        const showValue = !compact && v > 0 && (labelKeys.includes(d.key) || active === i)
                        return (
                            <g key={d.key}>
                                {v > 0 && <path d={barPath(x, y(v), barW, h)} className={'mv-chart-bar' + (dim ? ' is-dim' : '')} />}
                                {showValue &&
                                    <text x={x + barW / 2} y={y(v) - 6} className='mv-chart-value' textAnchor='middle'>{v.toLocaleString('en-IN')}</text>}
                                {!compact && i % every === (data.length - 1) % every &&
                                    <text x={axisW + band * i + band / 2} y={height - 6} className='mv-chart-tick' textAnchor='middle'>{d.label}</text>}
                                {/* hit target: the whole column band, taller and wider than the bar */}
                                <rect x={axisW + band * i} y={0} width={band} height={height - axisH} fill='transparent'
                                    onMouseEnter={() => setActive(i)} onClick={() => setActive(i)} />
                            </g>
                        )
                    })}
                    <line x1={axisW} x2={width} y1={top + plotH} y2={top + plotH} className='mv-chart-base' />
                </svg>}

            {tip && !compact &&
                <div className='mv-chart-tip' role='status'
                    style={{ left: Math.min(Math.max(tipX, 70), Math.max(70, width - 70)) }}>
                    <strong>{tip.title}</strong>
                    <span>{(tip.value || 0).toLocaleString('en-IN')} {unit}{tip.detail ? ' · ' + tip.detail : ''}</span>
                </div>}

            <table className='mv-visually-hidden'>
                <caption>{ariaLabel}</caption>
                <tbody>
                    {data.map(d =>
                        <tr key={d.key}><th scope='row'>{d.title}</th><td>{d.value} {unit}{d.detail ? ', ' + d.detail : ''}</td></tr>
                    )}
                </tbody>
            </table>
        </div>
    )
}

export default BarChart
